// @givmo/passkeys — the Sign-in & security card's "Authenticator app" and "Recovery codes" rows
// (AS-WEB14; D-AS-21's A4, its words approved in the round-2 sweep, D-AS-35 D3).
//
// Drawn only while the backend serves authenticator apps here (`capabilities.mfa.enabled`, dark in
// production until D-AS-27 R11's last step), from the server's own record of the account
// (`GET /users/me/security`'s `mfa`):
//   - an authenticator ON: "On since {date}", and "Turn off" for an account the web offers
//     authenticators to (its Google, Apple or Microsoft way runs the sheet the change needs); the
//     recovery codes: "{n} left · Created {date}" and "Get new codes";
//   - OFF: "Off" and "Set up", ONLY for an account the web offers one to (D-AS-26 N2). A password,
//     emailed-code or phone-only account never meets the row at all — no offer, and no teaser.
//
// TURN OFF asks first (iOS's confirmation, without its untrue "your password alone"), then runs
// through the gate exactly as setting up does: a fresh Google, Apple or Microsoft session, else the
// sheet with those ways only. On an account with an authenticator that re-sign-in owes the app's
// code, so the change is confirmed with the factor it removes — and "Lost your authenticator?" leads
// to the reset. Then the server's mirror is synced.
//
// GET NEW CODES asks first (iOS's), then replaces the set through the backend's own step-up
// (`step_up_required`, action `regenerate_recovery_codes`, live with `STEP_UP_AUTH_ENABLED`), and
// shows the new set once.
//
// UNDER A SIGN-IN LOCK (AS-WEB23, frame E1; `locked`, the card's one reading): an authenticator that
// is on and its recovery codes stay listed as plain facts, since the lock pauses them and deletes
// nothing, with no Turn off and no Get new codes (each would end in a confirmation the lock refuses).
// An authenticator that is off has nothing left to say, so its row goes with its Set up.

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, ErrorMessage, testIdProp } from '@givmo/design-system';
import { sdk, type RecoveryCodesOut } from '@givmo/api-client';
import { isStepUpCancelled, useOptionalStepUpHost, useStepUpGate } from '@givmo/step-up';
import {
  authenticatorAppsServedFrom,
  invalidateAccountFactors,
  servedData,
  useMySecurity,
} from './account-factors';
import { authenticatorAppOffered } from './authenticator-eligibility';
import {
  AUTHENTICATOR_STEP_UP_ROWS as ROWS,
  AUTHENTICATOR_WORDS as WORDS,
  authenticatorOnDetail,
  recoveryCodesDetail,
} from './authenticator-copy';
import {
  AUTHENTICATOR_STEP_UP_COPY,
  authenticatorClientRefusal,
  authenticatorFailureLine,
  requireProviderSession,
  useAuthenticatorSetup,
  type AuthenticatorControls,
} from './authenticator-setup';
import { RecoveryCodesView } from './recovery-codes-view';

const ROW = 'flex flex-wrap items-start justify-between gap-x-16 gap-y-10 px-20 py-16';
const ROW_TITLE = 'font-sans text-s14 font-w600 tracking-givmo text-text-strong';
const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';

function ConfirmDialog({
  title,
  line,
  action,
  testId,
  onCancel,
  onConfirm,
}: {
  title: string;
  line: string;
  action: string;
  testId: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <Dialog.Content aria-label={title} className="flex flex-col gap-16" testId={testId}>
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>
        <Dialog.Description className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
          {line}
        </Dialog.Description>
        <div className="flex justify-end gap-8">
          <Button appearance="saas" tone="secondary" onClick={onCancel} testId={`${testId}-cancel`}>
            {WORDS.cancel}
          </Button>
          <Button appearance="saas" tone="danger" onClick={onConfirm} testId={`${testId}-confirm`}>
            {action}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export interface AuthenticatorRowsProps {
  uid: string | null;
  /** The console's enrolment primitives; absent, no authenticator action is drawn. */
  controls: AuthenticatorControls | undefined;
  /** A sign-in lock binds the account (AS-WEB23): the state alone, no action. */
  locked?: boolean;
}

export function AuthenticatorRows({ uid, controls, locked = false }: AuthenticatorRowsProps) {
  const queryClient = useQueryClient();
  const host = useOptionalStepUpHost();
  const security = servedData(useMySecurity(uid));
  const setup = useAuthenticatorSetup(controls, { testIdPrefix: 'authenticator-card-step-up' });
  const turnOffRefusal = useMemo(() => authenticatorClientRefusal(ROWS.turnOff), []);
  const turnOffGate = useStepUpGate({
    copy: AUTHENTICATOR_STEP_UP_COPY,
    testIdPrefix: 'authenticator-card-step-up',
    clientRefusal: turnOffRefusal,
  });
  const replaceGate = useStepUpGate({
    copy: AUTHENTICATOR_STEP_UP_COPY,
    testIdPrefix: 'authenticator-card-step-up',
  });
  const [confirming, setConfirming] = useState<'turn-off' | 'replace' | null>(null);
  const [turningOff, setTurningOff] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [replaced, setReplaced] = useState<RecoveryCodesOut | null>(null);

  if (!authenticatorAppsServedFrom(security) || security === undefined) return null;
  const mfa = security.mfa;
  const on = mfa.totp_enrolled === true;
  // Actions need the console's primitives AND an account the web offers authenticators to, and no lock.
  const offered =
    !locked && controls !== undefined && authenticatorAppOffered(host?.waysIn ?? [], security);
  if (!on && !offered) return null;

  const turnOff = async () => {
    setConfirming(null);
    if (controls === undefined) return;
    setTurningOff(true);
    setFailure(null);
    try {
      await turnOffGate.run(async () => {
        await requireProviderSession(controls);
        await controls.turnOff();
      });
      await sdk.userSecuritySyncMyFactors({ throwOnError: true }).catch(() => undefined);
    } catch (thrown) {
      if (!isStepUpCancelled(thrown)) setFailure(authenticatorFailureLine(thrown));
    } finally {
      setTurningOff(false);
      void invalidateAccountFactors(queryClient);
    }
  };

  const replace = async () => {
    setConfirming(null);
    setFailure(null);
    try {
      const { data } = await replaceGate.run(() =>
        sdk.userSecurityRegenerateMyRecoveryCodes({ throwOnError: true }),
      );
      setReplaced(data);
    } catch (thrown) {
      if (!isStepUpCancelled(thrown)) setFailure(authenticatorFailureLine(thrown));
    }
  };

  const setUp = async () => {
    setFailure(null);
    // A failure before the secret is the hook's own line (`setup.failure`), shown below.
    await setup.start();
  };
  // A failure belongs to an action, and a lock leaves none to retry.
  const shownFailure = locked ? null : (failure ?? setup.failure);

  return (
    <>
      <div className={ROW} {...testIdProp('sign-in-security-authenticator')}>
        <div className="flex min-w-0 flex-col gap-2">
          <h4 className={ROW_TITLE}>{WORDS.rowTitle}</h4>
          <p className={QUIET} {...testIdProp('sign-in-security-authenticator-detail')}>
            {on ? authenticatorOnDetail(mfa.totp_enrolled_at) : WORDS.off}
          </p>
        </div>
        {offered && (
          <div className="shrink-0">
            {on ? (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                loading={turningOff}
                onClick={() => setConfirming('turn-off')}
                testId="sign-in-security-authenticator-turn-off"
              >
                {WORDS.turnOff}
              </Button>
            ) : (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                loading={setup.pending}
                onClick={() => void setUp()}
                testId="sign-in-security-authenticator-set-up"
              >
                {WORDS.setUp}
              </Button>
            )}
          </div>
        )}
        {shownFailure !== null && (
          <ErrorMessage className="w-full pt-0" testId="sign-in-security-authenticator-failed">
            {shownFailure}
          </ErrorMessage>
        )}
      </div>
      {on && (
        <div className={ROW} {...testIdProp('sign-in-security-recovery-codes')}>
          <div className="flex min-w-0 flex-col gap-2">
            <h4 className={ROW_TITLE}>{WORDS.codesRowTitle}</h4>
            <p className={QUIET} {...testIdProp('sign-in-security-recovery-codes-detail')}>
              {recoveryCodesDetail(
                mfa.recovery_codes_remaining ?? 0,
                mfa.recovery_codes_generated_at,
              )}
            </p>
          </div>
          {!locked && (
            <div className="shrink-0">
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => setConfirming('replace')}
                testId="sign-in-security-recovery-codes-new"
              >
                {WORDS.newCodes}
              </Button>
            </div>
          )}
        </div>
      )}
      {confirming === 'turn-off' && (
        <ConfirmDialog
          title={WORDS.turnOffTitle}
          line={WORDS.turnOffLine}
          action={WORDS.turnOff}
          testId="authenticator-turn-off-confirm"
          onCancel={() => setConfirming(null)}
          onConfirm={() => void turnOff()}
        />
      )}
      {confirming === 'replace' && (
        <ConfirmDialog
          title={WORDS.replaceTitle}
          line={WORDS.replaceLine}
          action={WORDS.replace}
          testId="recovery-codes-replace-confirm"
          onCancel={() => setConfirming(null)}
          onConfirm={() => void replace()}
        />
      )}
      {replaced !== null && (
        <Dialog.Root open>
          <Dialog.Content
            width={560}
            className="flex flex-col gap-16"
            testId="recovery-codes-dialog"
          >
            <RecoveryCodesView
              codes={replaced.recovery_codes}
              onSaved={() => {
                setReplaced(null);
                void invalidateAccountFactors(queryClient);
              }}
            />
          </Dialog.Content>
        </Dialog.Root>
      )}
      {setup.dialog}
      {turnOffGate.dialog}
      {replaceGate.dialog}
    </>
  );
}
